import { FC, useContext, useMemo, useState } from "react";
import { StatusListItemTooltip } from "../lists/status/StatusListStats";
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogProps, DialogTitle, Paper } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { AppSettings } from "@/AppSettings";
import { Link, useParams } from "react-router-dom/dist";
import SourceDataContext from "@/contexts/data/SourceDataContext";
import { SourceDataEventStatus, SourceDataFormStatus } from "@/api/dtos";
import TrialContext from "@/contexts/data/TrialContext";
import SourceDataFormSettingsContext from "@/contexts/form/SourceDataFormSettingsContext";
import { ParticipantContext } from "@/contexts/data/ParticipantContext";

interface IOpenClinicaParticipantLinkProps {
    studyNumber?: string;
    studyEventOid?: string;
    studyEventRepeat?: number;
    crfOid?: number;
}

interface IOpenClinicaParticipantLinkDialogProps extends DialogProps {
    url?: string;
    handleClose: () => void;
}

const OpenClinicaParticipantLinkDialog: FC<IOpenClinicaParticipantLinkDialogProps> = ({
    url,
    handleClose,
    ...props
}) => {
    const { data: trial } = useContext(TrialContext)

    const warningContent = useMemo(() => {
        return trial ?
            `Please ensure that you have selected the appropriate study (${trial.name}) in OpenClinica before continuing. Failure to do so may result in unexpected behaviour.` :
            "Please ensure that you have selected the appropriate study in OpenClinica before continuing. Failure to do so may result in unexpected behaviour.";
    }, [trial]);

    if(!url) {
        return null;
    }

    return (
        <Dialog
            {...props}
        >
            <DialogTitle>Confirm</DialogTitle>
            <DialogContent>
                <Alert 
                    severity="warning"
                >
                    {warningContent}
                </Alert>
            </DialogContent>
            <DialogActions>
                <Button
                    onClick={handleClose}
                >
                    Cancel
                </Button>
                <Button
                    color="primary"
                    component={Link}
                    target="_blank"
                    to={url}
                    onClick={handleClose}
                >
                    Continue
                </Button>
            </DialogActions>
        </Dialog>
    );
}

const OpenClinicaParticipantLink: FC<IOpenClinicaParticipantLinkProps> = (props) => {
    const [dialogOpen, setDialogOpen] = useState(false);

    const {data: participant} = useContext(ParticipantContext);

    const ocUrlAction = useMemo(() => {
        return `ViewStudySubject?id=${participant?.openClinicaStudySubjectId}`
    }, [participant]);

    const url = useMemo(() => `${AppSettings.openClinicaUrl}OpenClinica/${ocUrlAction}`, [AppSettings.openClinicaUrl, ocUrlAction]);
        
    
        const { 
            settings: { 
                hideOpenClinicaLinkWarning
            } 
        } = useContext(SourceDataFormSettingsContext);

    if(!participant?.openClinicaStudySubjectId || !ocUrlAction) {
        return null;
    }

    return (
        <>
            <OpenClinicaParticipantLinkDialog 
                open={dialogOpen}
                url={url}
                handleClose={() => setDialogOpen(false)}
            />
            <StatusListItemTooltip 
                title={
                    <>
                        <strong>Open Participant in OpenClinica</strong>
                    </>
                }
            >
                <Paper>
                    {
                        hideOpenClinicaLinkWarning ?
                            (
                                <Button
                                    component="a"
                                    href={url}
                                    target="_blank"
                                    color="inherit"
                                    disabled={!participant?.openClinicaStudySubjectId}
                                    sx={{
                                        minWidth: 0
                                    }}
                                >
                                    <FontAwesomeIcon icon={AppSettings.icons.externalLink} size="lg"  />
                                </Button>
                            ) :
                            (
                                <Button
                                    color="inherit"
                                    disabled={!participant?.openClinicaStudySubjectId}
                                    onClick={() => setDialogOpen(true)}
                                    sx={{
                                        minWidth: 0
                                    }}
                                >
                                    <FontAwesomeIcon icon={AppSettings.icons.externalLink} size="lg"  />
                                </Button>
                            )
                    }
                </Paper>
            </StatusListItemTooltip>
        </>
    )
}

export default OpenClinicaParticipantLink;